import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SelectionModel } from '@angular/cdk/collections';
import {
  auditTime,
  catchError,
  filter,
  forkJoin,
  map,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FileUploadDialogComponent } from '../../global-components/file-upload-dialog/file-upload-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ConfigService as AppConfigService } from '../../../../../my-app/src/app/product-health/services/config.service';
import { PopupTableComponent } from '../../global-components/popup-table/popup-table.component';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-dynamic-table-user-history',
  imports: [
    CommonModule,
    MatTableModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    NgxDatatableModule,
    MatTooltipModule,
    MatRadioModule,
    MtxTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './dynamic-table-user-history.component.html',
  styleUrl: './dynamic-table-user-history.component.scss',
})
export class DynamicTableUserHistoryComponent {
  @Input() rows: any[] = [];
  @Input() isEditMode = false;
  @Input() data!: any;
  selectedFilters: any = []; // default
  @Output() rowsSelected = new EventEmitter<any[]>();
  columnMode: ColumnMode = ColumnMode.force;
  Number = Number;
  // (legacy) selection model — kept to avoid breaking other code
  selection = new SelectionModel<any>(true, []);

  // our own selection tracking for header/row checkboxes
  selectedRows: any[] = [];
  selectAllChecked = false;

  private destroy$ = new Subject<void>();
  public hasError = false;
  public errorMessage = '';
  filters: any;
  totalBudget = 0;
  isLoading = true;
  fullData: any;
  selectedFiles: File[] = [];
  note: string = '';
  changed = false;
  originalRows: any[] = [];
  showInvalidOnly = false;
  columnFilters: any = {};
  dropdownSearchText: Record<string, string> = {};
  private displayedColumnsCache: { key: string; value: string[] } | null = null;
  private renderedColumnsCache: { key: string; value: any[] } | null = null;
  private dropdownDisplayCache: Record<string, any[]> = {};
  private dropdownVisibilityCache: Record<string, boolean> = {};
  private headerClassCache: Record<string, string> = {};
  private cellClassCache: Record<string, string> = {};
  private rowHeightCache = new WeakMap<object, number>();
  private cacheReloadTimer: ReturnType<typeof setInterval> | null = null;
  private readonly cacheReloadMs = 10 * 60 * 1000;
  email: string = '';
  pinnedColumnProp: string | null = null;
  private sortState: { prop: string | null; dir: 'asc' | 'desc' | null } = {
    prop: null,
    dir: null,
  };
  rowIdentity = (row: any): unknown => row?.uuid ?? row;
  readonly tableRowHeight = (row?: any): number => this.getTableRowHeight(row);
  readonly auditHistoryIcon =
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231f1e1e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='9'/><polyline points='12 7 12 12 16 14'/></svg>";
  private readonly projectScoreProps = new Set([
    'ideation_final_score',
    'simulation_final_score',
    'aop_final_score',
    'final_score',
  ]);
  private hasHandledAuditDeepLink = false;
  private userHistoryMergedHeaderConfig: any = null;

  getDefaultColumnWidth(): number {
    const configuredWidth = Number(this.data?.defaultColumnWidth);
    return Number.isFinite(configuredWidth) && configuredWidth > 0
      ? configuredWidth
      : 200;
  }

  constructor(
    private configService: ConfigService,
    private appConfigService: AppConfigService,
    private filterState: GlobalService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private keycloakService: KeycloakService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  ngAfterViewInit() {
    this.filterState.filters$
      .pipe(
        // short delay to collect rapid emissions
        auditTime(1000),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        this.filters = { ...filters.filter };
        this.columnFilters = {};
        this.clearUiCaches();
        this.loadAndRenderChart(filters.filter);
      });
  }

  ngOnInit() {
    this.startCacheReloadTimer();
    this.checkEditRoleAccess();
  }

  private startCacheReloadTimer(): void {
    this.stopCacheReloadTimer();
    this.cacheReloadTimer = setInterval(() => {
      this.clearUiCaches();
    }, this.cacheReloadMs);
  }

  private stopCacheReloadTimer(): void {
    if (this.cacheReloadTimer !== null) {
      clearInterval(this.cacheReloadTimer);
      this.cacheReloadTimer = null;
    }
  }

  private clearUiCaches(): void {
    this.uniqueValuesCache = {};
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.headerClassCache = {};
    this.cellClassCache = {};
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
  }

  checkEditRoleAccess() {
    this.email =
      this.keycloakService.getKeycloakInstance().idTokenParsed?.['email'];
    this.configService.getRole(this.email).subscribe((role: any) => {
      if (this.data.editRoles?.includes(role) && this.data.editMode) {
        this.data.editMode = true;
      } else {
        this.data.editMode = false;
      }
    });
  }

  /** Safely parse numbers like "5,200", "$1,234.50", "(1,000)", etc. */
  private parseNumeric(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;

    let s = String(value).trim();
    if (!s) return 0;

    // Handle accounting negatives like "(1,234.56)"
    const isParenNegative = s.startsWith('(') && s.endsWith(')');

    // Remove commas, currency, and any non digit/.- characters (keep minus & dot)
    s = s.replace(/,/g, '');
    s = s.replace(/[^\d.-]/g, '');

    let n = parseFloat(s);
    if (isNaN(n)) n = 0;

    return isParenNegative ? -Math.abs(n) : n;
  }

  private isErrorPayload(payload: any): boolean {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return false;
    }

    const status = String(payload.status ?? '').toUpperCase();
    const code = Number(payload.code);
    return status === 'ERROR' || (!Number.isNaN(code) && code >= 400);
  }

  private getResponseErrorMessage(primaryPayload: any, res: any): string {
    if (this.isErrorPayload(primaryPayload) && primaryPayload?.message) {
      return String(primaryPayload.message);
    }

    const responseMessage =
      res?.responseList?.[0]?.message ??
      res?.message ??
      res?.error?.message;

    return responseMessage ? String(responseMessage) : '';
  }

  private normalizeMultiSelectValue(value: any): any[] {
    if (value === null || value === undefined || value === '') return [];

    if (Array.isArray(value)) {
      return value
        .flatMap((item) => this.normalizeMultiSelectValue(item))
        .filter((item) => item !== null && item !== undefined && item !== '');
    }

    if (typeof value !== 'string') {
      return [value];
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return [];

    const parsedJson = this.tryParseJson(trimmed);
    if (parsedJson !== null) {
      return this.normalizeMultiSelectValue(parsedJson);
    }

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      const inner = trimmed.slice(1, -1).trim();
      if (!inner) return [];

      return inner
        .split(',')
        .map((item) => this.stripOuterQuotes(item.trim()))
        .filter((item) => item.length > 0);
    }

    return [this.stripOuterQuotes(trimmed)];
  }

  private tryParseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private stripOuterQuotes(value: string): string {
    let normalized = value;
    while (
      normalized.length >= 2 &&
      ((normalized.startsWith('"') && normalized.endsWith('"')) ||
        (normalized.startsWith("'") && normalized.endsWith("'")))
    ) {
      normalized = normalized.slice(1, -1).trim();
    }
    return normalized;
  }

  formatCellValue(value: any): string {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== null && item !== undefined && item !== '')
        .join(', ');
    }
    if (value === null || value === undefined) return '';
    return String(value);
  }

  getNumberDisplayFormat(prop: string | null | undefined): string {
    const normalizedProp = String(prop || '').toLowerCase();
    if (normalizedProp.includes('investment')) {
      return '1.4-4';
    }
    return this.projectScoreProps.has(String(prop || '')) ? '1.1-1' : '1.0-4';
  }

  getMultiSelectTooltip(value: any): string {
    const selectedValues = this.normalizeMultiSelectValue(value);
    if (!selectedValues.length) return '';

    return selectedValues
      .map((item) => `• ${String(item)}`)
      .join('\n');
  }

  shouldShowHeaderInfo(column: any): boolean {
    return (
      !!column?.info ||
      !!column?.tooltipText ||
      !!column?.tooltip ||
      !!column?.tooltipUrl
    );
  }

  shouldShowHeaderImageTooltip(column: any): boolean {
    return !!column?.tooltipUrl || column?.tooltip === 'imageTooltip';
  }

  getHeaderTooltipText(column: any): string {
    return String(column?.tooltipText || column?.name || '').trim();
  }

  // Toggle one row
  toggleRow(row: any) {
    const idx = this.selectedRows.indexOf(row);
    if (idx >= 0) {
      this.selectedRows.splice(idx, 1); // unselect
    } else {
      this.selectedRows.push(row); // select
    }
    // keep header checkbox in sync
    this.selectAllChecked =
      this.rows?.length > 0 && this.selectedRows.length === this.rows.length;
    // recompute total
    this.updateTotalBudget();
    // emit
    this.rowsSelected.emit(this.selectedRows);
  }

  // Check if a row is selected
  isSelected(row: any): boolean {
    return this.selectedRows.indexOf(row) >= 0;
  }

  // Select/unselect all rows
  toggleSelectAll(checked: boolean) {
    this.selectAllChecked = checked;
    if (checked) {
      this.selectedRows = [...this.rows];
    } else {
      this.selectedRows = [];
    }
    this.updateTotalBudget();
    this.rowsSelected.emit(this.selectedRows);
  }

  // For indeterminate UI if you later switch header binding to [indeterminate]
  isAllSelected(): boolean {
    return (
      this.rows?.length > 0 && this.selectedRows.length === this.rows.length
    );
  }

  isIndeterminate(): boolean {
    return this.selectedRows.length > 0 && !this.isAllSelected();
  }

  NotInM: boolean = false;

  private loadAndRenderChart(filters?: any): void {
    this.isLoading = true;
    if (this.data.multiHeader) {
      this.getHeaderConfigForHistory().subscribe({
        next: (res: any) => {
          const sourceValue = Array.isArray(filters?.source)
            ? String(filters.source[0] ?? '').toLowerCase()
            : '';
          const fallbackKey = Object.keys(res || {})[0] || '';
          const resolvedKey = res?.[sourceValue] ? sourceValue : fallbackKey;
          const headerConfig = res?.[resolvedKey];
          this.userHistoryMergedHeaderConfig = res;

          if (headerConfig) {
            this.data.attributeNames = headerConfig.attributes || [];
            this.data.headers = headerConfig.headers || [];
            this.injectUserHistoryAuditColumns();
            this.resetPinnedColumnState();

            // Set `NotInM` to true if any column name starts with "Investment Estimate" or "Investment Split"
            this.NotInM = this.data.headers.some(
              (col: any) =>
                col.name?.startsWith('Investment Estimate') ||
                col.name?.startsWith('Investment Split')
            );

            // Merge additional filters and load rows
            this.filters = {
              ...this.filters,
              ...(this.data.additionalFilters || []).reduce(
                (acc: any, obj: any) => ({ ...acc, ...obj }),
                {}
              ),
            };
            this.getRows();
          } else {
            this.data.attributeNames = [];
            this.data.headers = [];
            this.resetPinnedColumnState();
            this.NotInM = false;
          }
        },
        error: () => {
          this.NotInM = false;
          this.isLoading = false;
        },
      });
    } else {
      this.getRows();
      this.NotInM = false;
    }
  }

  private getHeaderConfigForHistory(): Observable<any> {
    if (!this.isUserHistoryPage()) {
      return this.configService.getConfigData(this.data.headerUrl);
    }

    const headerFiles = [
      'assets/header-attribute/ideas-hopper.json',
      'assets/header-attribute/aop-planning.json',
      'assets/header-attribute/sim-planning.json',
    ];

    return forkJoin(
      headerFiles.map((path) => this.configService.getConfigData(path))
    ).pipe(
      // Merge by source type key (vpcr, vpi, pps/implementation, fap, ...)
      // and keep first-seen column order while appending unique extras.
      map((payloads: any[]) => this.mergeHeaderPayloads(payloads)),
      // fallback to ideas hopper schema if any one file fails
      catchError(() => this.configService.getConfigData(this.data.headerUrl))
    );
  }

  private mergeHeaderPayloads(payloads: any[]): any {
    const merged: any = {};

    for (const payload of payloads || []) {
      if (!payload || typeof payload !== 'object') continue;

      for (const key of Object.keys(payload)) {
        const sourceConfig = payload[key] || {};
        const existing = merged[key] || { headers: [], attributes: [] };

        const existingProps = new Set(
          (existing.headers || []).map((col: any) => String(col?.prop || ''))
        );
        const incomingHeaders = Array.isArray(sourceConfig.headers)
          ? sourceConfig.headers
          : [];
        for (const header of incomingHeaders) {
          const prop = String(header?.prop || '');
          if (!prop || existingProps.has(prop)) continue;
          existing.headers.push(header);
          existingProps.add(prop);
        }

        const existingAttributes = new Set(
          (existing.attributes || []).map((attr: any) => String(attr || ''))
        );
        const incomingAttributes = Array.isArray(sourceConfig.attributes)
          ? sourceConfig.attributes
          : [];
        for (const attr of incomingAttributes) {
          const attribute = String(attr || '');
          if (!attribute || existingAttributes.has(attribute)) continue;
          existing.attributes.push(attribute);
          existingAttributes.add(attribute);
        }

        merged[key] = existing;
      }
    }

    return merged;
  }

  /** Recalculate total budget from selected rows' total_cost (robust to strings with commas) */
  private updateTotalBudget() {
    this.totalBudget = this.selectedRows.reduce((sum, row) => {
      const estimateValue = this.isSimulationPlanningPage()
        ? row?.simulation_investment_estimate
        : this.isAopPlanningPage()
          ? row?.aop_investment_estimate
          : this.isIdeasHopperPage()
            ? row?.ideation_investment_estimate
            : row?.investment_estimate;
      const estimateCost = this.parseNumeric(estimateValue);

      // Prefer page-specific estimate values when available.
      if (estimateCost !== 0) {
        return sum + estimateCost;
      }

      if (this.NotInM) {
        const cost1 = this.parseNumeric(row?.investment_testing);
        return sum + cost1;
      } else {
        const cost = this.parseNumeric(row?.total_cost);
        // Legacy tables expose total_cost in dollars; convert to million dollars.
        const finalCost = cost / 1_000_000;
        return sum + finalCost;
      }
    }, 0);

    // Format the total budget with 1 decimal place
    this.totalBudget = parseFloat(this.totalBudget.toFixed(1));
  }

  availableBudget: any;

  @ViewChild('table') table: any;

  onBudgetInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.availableBudget = value;
    // Force table refresh
    if (this.table) {
      this.table.recalculate();
      this.table.bodyComponent.updateRows();
    }
    this.computeHighlightIndex();
    this.cdr.detectChanges();
  }

  highlightIndex = -1;
  indexMap = new Map<string, number>();

  private hasRowField(field: string): boolean {
    return this.rows?.some((row: any) => row && row[field] !== undefined);
  }

  private resolveScoreKey(): string {
    const headerProps = new Set(
      (this.data?.headers || []).map((column: any) => String(column?.prop || ''))
    );

    const scoreCandidates = [
      'aop_final_score',
      'ideation_final_score',
      'simulation_final_score',
      'final_score',
    ];

    for (const key of scoreCandidates) {
      if (headerProps.has(key) || this.hasRowField(key)) {
        return key;
      }
    }

    return 'final_score';
  }

  private resolveHighlightBudgetKey(): string {
    const configured = String(this.data?.highlightKey || '').trim();
    if (configured) {
      return configured;
    }

    const budgetCandidates = [
      'aop_investment_estimate',
      'ideation_investment_estimate',
      'simulation_investment_estimate',
      'investment_estimate',
      'total_cost',
    ];

    for (const key of budgetCandidates) {
      if (this.hasRowField(key)) {
        return key;
      }
    }

    return '';
  }

  computeHighlightIndex() {
    const budgetKey = this.resolveHighlightBudgetKey();
    const scoreKey = this.resolveScoreKey();

    if (
      this.availableBudget == null ||
      !budgetKey ||
      !this.rows?.length
    ) {
      this.highlightIndex = -1;
      this.indexMap.clear();
      return;
    }

    // Sort rows by the best available score field for this table/page.
    this.rows.sort((a, b) => {
      const scoreA = parseFloat(a?.[scoreKey]);
      const scoreB = parseFloat(b?.[scoreKey]);
      if (isNaN(scoreA)) return 1;
      if (isNaN(scoreB)) return -1;
      return scoreB - scoreA;
    });

    let total = 0;
    let lastIndex = -1;

    for (let i = 0; i < this.rows.length; i++) {
      const rawValue = this.rows[i][budgetKey];
      if (rawValue == null || isNaN(rawValue)) continue;

      const value = parseFloat(Number(rawValue).toFixed(4));
      if (isNaN(value)) continue;
      const newTotal = total + value;
      if (newTotal > this.availableBudget) {
        break;
      }

      total = newTotal;
      lastIndex = i;
    }

    this.highlightIndex = lastIndex;

    // Build fast lookup map: uuid -> rowIndex
    this.indexMap.clear();
    this.rows.forEach((row, i) => {
      this.indexMap.set(row.uuid, i);
    });

    // Force table refresh if needed
    this.rows = [...this.rows];
  }

  // Called by ngx-datatable for each row
  getRowClass = (row: any) => {
    if (this.highlightIndex === -1 || !row?.uuid) return '';

    const index = this.indexMap.get(row.uuid);
    if (index === undefined) return '';

    return index <= this.highlightIndex ? 'highlight-row' : '';
  };

  getRows() {
    const useRawPayload = this.data?.useRawPayload === true;
    if (!this.data?.apiUrl || (!useRawPayload && !this.data?.functionName)) {
      this.rows = [];
      this.originalRows = [];
      this.isLoading = false;
      return;
    }

    if (useRawPayload) {
      const rawPayload = this.buildUserHistoryRawPayload();
      this.configService
        .customApi(this.data.apiUrl, rawPayload, this.data, 'post')
        .subscribe({
          next: (res: any) => {
            const extractedRows =
              res?.responseList?.[0]?.responseData?.[0]?.[this.data?.respKey] ??
              res?.responseList?.[0]?.responseData ??
              res?.responseData ??
              res ??
              [];
            this.rows = Array.isArray(extractedRows) ? extractedRows : [];
            this.rows = this.rows.map((r: any) => ({ ...r, row_status: 'unchanged' }));
            this.originalRows = this.cloneRows(this.rows);
            this.clearUiCaches();
            this.selectedRows = [];
            this.selectAllChecked = false;
            this.totalBudget = 0;
            this.fullData = res ?? [];
            this.hasError = !this.rows?.length;
            this.errorMessage = this.hasError
              ? 'No data found for the selected filters.'
              : '';
            this.isLoading = false;
            this.computeHighlightIndex();
            this.enforceUserHistoryLatestSort();
            this.applyFilters();
            this.scheduleTableRecalculate();
            this.maybeOpenAuditHistoryFromNavigation();
          },
          error: () => {
            this.hasError = true;
            this.isLoading = false;
            this.errorMessage = 'No Data available for the table.';
          },
        });
      return;
    }

    this.data.costFilter = this.data.costFilter ? this.selectedFilters : null;
    this.configService
      .fetchData(
        this.data.apiUrl,
        this.data.functionName,
        this.filters,
        this.data,
        'post',
        undefined,
        this.email
      )
      .subscribe({
        next: (res) => {
          const primaryPayload = res?.responseList?.[0]?.responseData?.[0];
          const responseErrorMessage = this.getResponseErrorMessage(
            primaryPayload,
            res
          );
          const extractedRows = this.data.respKey
            ? primaryPayload?.[this.data.respKey] ??
            res?.responseList?.[0]?.[this.data.respKey] ??
            res?.[this.data.respKey] ??
            []
            : res ?? [];

          this.rows = Array.isArray(extractedRows) ? extractedRows : [];

          this.rows = this.rows.map((r: any) => {
            // make a shallow copy
            const updatedRow = { ...r, row_status: 'unchanged' };

            // Normalize per-column values for stable edit-mode rendering.
            this.data.headers.forEach((col: any) => {
              if (col.isMultiSelect) {
                const val = updatedRow[col.prop];
                updatedRow[col.prop] = this.normalizeMultiSelectValue(val);
                return;
              }

              // Keep investment fields at max 4 decimals after fetch/upload refresh.
              if (
                col?.type === 'number' &&
                String(col?.prop || '').toLowerCase().includes('investment')
              ) {
                const rawVal = updatedRow[col.prop];
                if (rawVal === '' || rawVal === null || rawVal === undefined) {
                  return;
                }

                const numericValue = Number(rawVal);
                if (Number.isFinite(numericValue)) {
                  updatedRow[col.prop] = this.roundToMaxDecimals(numericValue, 4);
                }
              }
            });

            return updatedRow;
          });

          this.originalRows = this.cloneRows(this.rows);
          this.clearUiCaches();
          // reset selection + totals whenever data reloads
          this.selectedRows = [];
          this.selectAllChecked = false;
          this.totalBudget = 0;

          this.fullData = this.isErrorPayload(primaryPayload)
            ? []
            : primaryPayload ?? [];
          const isEmpty = !this.rows || !this.rows?.length;
          this.isLoading = false;
          if (isEmpty) {
            this.hasError = true;
            this.errorMessage =
              responseErrorMessage || 'No data found for the selected filters.';
          } else {
            this.hasError = false;
          }
          this.computeHighlightIndex();
          this.enforceUserHistoryLatestSort();
          this.applyFilters();
          this.scheduleTableRecalculate();
          this.maybeOpenAuditHistoryFromNavigation();
        },
        error: () => {
          this.hasError = true;
          this.isLoading = false;
          this.errorMessage = 'No Data available for the table.';
        },
      });
  }

  private buildUserHistoryRawPayload(): any {
    const configuredPayload = this.data?.rawPayload;
    if (configuredPayload && typeof configuredPayload === 'object') {
      const payload = JSON.parse(JSON.stringify(configuredPayload));
      const filterList = Array.isArray(payload?.filterList) ? payload.filterList : [];
      const fromDateRaw =
        this.filters?.from_date?.[0] ??
        this.filters?.fromDate?.[0] ??
        this.filters?.start_date?.[0] ??
        '';
      const toDateRaw =
        this.filters?.to_date?.[0] ??
        this.filters?.toDate?.[0] ??
        this.filters?.end_date?.[0] ??
        '';
      const fromDateInput = fromDateRaw || '';
      const toDateInput = toDateRaw || '';
      const fromDate = this.formatAuditDateForPayload(fromDateInput, false);
      const toDate = this.formatAuditDateForPayload(toDateInput, true);
      const updatedByValues = this.normalizeMultiSelectValue(
        this.filters?.updated_by
      )
        .map((value: any) => String(value ?? '').trim())
        .filter((value: string) => !!value && value.toLowerCase() !== 'all');
      const tableNameRaw = String(this.filters?.table_name?.[0] ?? '').trim();
      const tableName = tableNameRaw.toUpperCase();
      const projectCategoryByTable: Record<string, string> = {
        IDEAS_HOPPER: 'Ideation',
        'IDEAS HOPPER': 'Ideation',
        SIM_PLANNING: 'Simulation',
        'SIM PLANNING': 'Simulation',
        AOP_PLANNING: 'AOP',
        'AOP PLANNING': 'AOP',
      };
      const mappedProjectCategory =
        projectCategoryByTable[tableName] || '';
      let hasFromDateFilter = false;
      let hasToDateFilter = false;

      filterList.forEach((item: any) => {
        const attributeName = String(item?.attributeName || '').trim();
        const operator = String(item?.operator || '').trim();
        if (attributeName === 'updated_by') {
          if (updatedByValues.length > 0) {
            item.attributeValue = updatedByValues;
          } else {
            item.attributeValue = [];
          }
        }

        if (attributeName !== 'updated_date') {
          return;
        }

        if (operator === '>=' && fromDate) {
          hasFromDateFilter = true;
          item.logicalOperator = 'AND';
          item.beginScope = ' (';
          item.endScope = '';
          item.attributeValue = [`'${fromDate}'`];
        }

        if (operator === '<=' && toDate) {
          hasToDateFilter = true;
          item.logicalOperator = 'AND';
          item.beginScope = '';
          item.endScope = ')';
          item.attributeValue = [`'${toDate}'`];
        }
      });

      if (!hasFromDateFilter && fromDate) {
        filterList.push({
          attributeName: 'updated_date',
          operator: '>=',
          logicalOperator: 'AND',
          beginScope: ' (',
          endScope: '',
          attributeValue: [`'${fromDate}'`],
        });
      }

      if (!hasToDateFilter && toDate) {
        filterList.push({
          attributeName: 'updated_date',
          operator: '<=',
          logicalOperator: 'AND',
          beginScope: '',
          endScope: ')',
          attributeValue: [`'${toDate}'`],
        });
      }

      payload.filterList = filterList.filter((item: any) => {
        const attributeName = String(item?.attributeName || '').trim();
        const hasProjectCategoryAttribute = attributeName === 'project_category';
        const hasProjectCategoryObjectKey =
          item &&
          typeof item === 'object' &&
          Object.prototype.hasOwnProperty.call(item, 'project_category');
        if (hasProjectCategoryAttribute || hasProjectCategoryObjectKey) {
          return false;
        }
        if (attributeName === 'updated_by') {
          const values = Array.isArray(item?.attributeValue)
            ? item.attributeValue.filter((value: any) => String(value ?? '').trim() !== '')
            : [];
          return values.length > 0;
        }
        if (attributeName === 'updated_date') {
          const values = Array.isArray(item?.attributeValue) ? item.attributeValue : [];
          return values.length > 0;
        }
        return true;
      });

      if (mappedProjectCategory) {
        payload.filterList.push({
          attributeName: 'project_category',
          operator: 'IN',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: [mappedProjectCategory],
        });
      }

      return payload;
    }

    const titleValue = String(this.data?.auditTitle || '').trim();
    const updatedByValue = String(this.data?.auditUpdatedBy || this.email || '').trim();
    const fromDateValue = this.formatAuditDateForPayload(this.data?.auditFromDate, false);
    const toDateValue = this.formatAuditDateForPayload(this.data?.auditToDate, true);

    return {
      entityName: 'ecosystem',
      attributesList: ['*'],
      filterList: [
        {
          attributeName: 'title',
          operator: 'IN',
          logicalOperator: 'AND',
          beginScope: '',
          endScope: '',
          attributeValue: [titleValue],
        },
        {
          attributeName: 'updated_by',
          operator: 'IN',
          logicalOperator: 'AND',
          beginScope: '',
          endScope: '',
          attributeValue: [updatedByValue],
        },
        {
          attributeName: 'updated_date',
          operator: '>=',
          logicalOperator: 'AND',
          beginScope: '(',
          endScope: '',
          attributeValue: [`'${fromDateValue}'`],
        },
        {
          attributeName: 'updated_date',
          operator: '<=',
          logicalOperator: '',
          beginScope: '',
          endScope: ')',
          attributeValue: [`'${toDateValue}'`],
        },
      ],
    };
  }

  enterEdit() {
    if (this.isEditMode) return;
    this.isEditMode = true;
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
    this.resetSelectionState();
    this.scheduleTableRecalculate();
  }

  exitEdit() {
    if (!this.isEditMode) return;
    this.isEditMode = false;
    this.rowHeightCache = new WeakMap<object, number>();
    this.displayedColumnsCache = null;
    this.renderedColumnsCache = null;
    this.resetSelectionState();

    // Fast path: switching tabs without edits should not trigger a network reload.
    if (!this.changed) {
      return;
    }

    // Discard unsaved edits locally by restoring the last loaded snapshot.
    this.rows = this.cloneRows(this.originalRows).map((row: any) => ({
      ...row,
      row_status: 'unchanged',
    }));
    this.changed = false;
    this.note = '';
    this.computeHighlightIndex();
    this.applyFilters();
    this.scheduleTableRecalculate();
  }

  private resetSelectionState(): void {
    this.selection.clear();
    this.selectedRows = [];
    this.selectAllChecked = false;
    this.totalBudget = 0;
  }

  private cloneRows(rows: any[]): any[] {
    if (!Array.isArray(rows)) return [];

    return rows.map((row: any) => {
      const clonedRow: any = {};

      Object.keys(row || {}).forEach((key) => {
        const value = row[key];
        if (Array.isArray(value)) {
          clonedRow[key] = [...value];
        } else if (value && typeof value === 'object') {
          clonedRow[key] = { ...value };
        } else {
          clonedRow[key] = value;
        }
      });

      return clonedRow;
    });
  }

  private sortRows(rows: any[], prop: string, dir: 'asc' | 'desc'): any[] {
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      if (this.isUserHistoryPage() && prop === 'updated_date') {
        const timeA = this.getLatestChangeTimestamp(a);
        const timeB = this.getLatestChangeTimestamp(b);
        if (timeA !== timeB) {
          return dir === 'asc' ? timeA - timeB : timeB - timeA;
        }
      }

      let valA = Array.isArray(a?.[prop]) ? a?.[prop].join(', ') : a?.[prop];
      let valB = Array.isArray(b?.[prop]) ? b?.[prop].join(', ') : b?.[prop];

      if (valA === null || valA === undefined || valA === '') return 1;
      if (valB === null || valB === undefined || valB === '') return -1;

      const numA = Number(valA);
      const numB = Number(valB);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        valA = numA;
        valB = numB;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return dir === 'asc' ? -1 : 1;
      if (valA > valB) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedRows;
  }

  private getLatestChangeTimestamp(row: any): number {
    const updated = this.toTimestamp(row?.updated_date ?? row?.updatedDate);
    if (updated > 0) {
      return updated;
    }
    return this.toTimestamp(row?.created_date ?? row?.createdDate);
  }

  private toTimestamp(value: any): number {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  private enforceUserHistoryLatestSort(): void {
    if (!this.isUserHistoryPage()) return;
    this.sortState = { prop: 'updated_date', dir: 'desc' };
    this.rows = this.sortRows(this.rows, 'updated_date', 'desc');
  }

  private applyCurrentSort(): void {
    if (!this.sortState.prop || !this.sortState.dir) {
      return;
    }

    const hasSortableColumn = (this.renderedColumns || []).some(
      (column: any) => String(column?.prop || '') === this.sortState.prop
    );

    if (!hasSortableColumn) {
      return;
    }

    this.rows = this.sortRows(this.rows, this.sortState.prop, this.sortState.dir);
  }

  sortColumn(column: any, event?: MouseEvent): void {
    event?.stopPropagation();

    if (!column?.prop || column.sortable === false) {
      return;
    }

    const nextDir: 'asc' | 'desc' =
      this.sortState.prop === column.prop && this.sortState.dir === 'asc'
        ? 'desc'
        : 'asc';

    this.onSort({ sorts: [{ prop: column.prop, dir: nextDir }] });
  }

  isColumnSorted(column: any): boolean {
    return !!column?.prop && this.sortState.prop === column.prop && !!this.sortState.dir;
  }

  getSortIcon(column: any): string {
    if (!this.isColumnSorted(column) || !this.sortState.dir) {
      return 'swap_vert';
    }

    return this.sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getSortTooltipText(column: any): string {
    if (!this.isColumnSorted(column)) {
      return 'Sort column';
    }

    return this.sortState.dir === 'asc' ? 'Sort descending' : 'Sort ascending';
  }

  private getTableRowHeight(row?: any): number {
    return this.isEditMode ? 56 : 44;
  }

  private estimateReadOnlyRowHeight(row: any): number {
    const minHeight = 44;
    const lineHeight = 18;
    const verticalPadding = 18;
    const maxLinesCap = 6;
    let maxLines = 1;

    for (const column of this.renderedColumns) {
      if (!column?.prop || this.isDeleteColumn(column)) {
        continue;
      }

      const cellText = this.getRowHeightCellText(row, column);
      if (!cellText) {
        continue;
      }

      const columnWidth = Number(column.width || this.getDefaultColumnWidth());
      const usableWidth = Math.max(72, columnWidth - 24);
      const charsPerLine = Math.max(10, Math.floor(usableWidth / 7));
      const lineCount = cellText
        .split(/\r?\n/)
        .reduce((total: number, line: string) => {
          const normalizedLine = line.trim();
          if (!normalizedLine) {
            return total + 1;
          }

          return total + Math.max(1, Math.ceil(normalizedLine.length / charsPerLine));
        }, 0);

      maxLines = Math.max(maxLines, Math.min(maxLinesCap, lineCount));
    }

    return Math.max(minHeight, verticalPadding + maxLines * lineHeight);
  }

  private getRowHeightCellText(row: any, column: any): string {
    const value = row?.[column?.prop];

    if (value === null || value === undefined) {
      return '';
    }

    if (column?.type === 'date') {
      return value ? String(value) : '';
    }

    if (column?.type === 'number') {
      return Number.isFinite(value) ? String(value) : String(value ?? '');
    }

    return this.formatCellValue(value);
  }

  get displayedColumns(): string[] {
    const headerSignature = (this.data?.headers || [])
      .map((col: any) => `${col?.prop}:${col?.showOnEdit ? 1 : 0}`)
      .join('|');
    const cacheKey = `${this.isEditMode ? 'edit' : 'view'}::${headerSignature}`;

    if (this.displayedColumnsCache?.key === cacheKey) {
      return this.displayedColumnsCache.value;
    }

    const cols = [
      'sno',
      ...(this.data?.headers || [])
        .filter((col: any) => !(col.showOnEdit && !this.isEditMode))
        .map((col: any) => col.prop),
    ];

    if (this.isEditMode) {
      cols.push('delete'); // only in edit mode
    }
    this.displayedColumnsCache = { key: cacheKey, value: cols };
    return cols;
  }

  get renderedColumns(): any[] {
    const headerSignature = (this.data?.headers || [])
      .map((col: any) => `${col?.prop}:${col?.showOnEdit ? 1 : 0}`)
      .join('|');
    const hasDeleteColumn = this.isEditMode && this.data?.buttons?.includes('delete');
    const cacheKey = `${this.isEditMode ? 'edit' : 'view'}::${headerSignature}::delete:${hasDeleteColumn ? 1 : 0}`;

    if (this.renderedColumnsCache?.key === cacheKey) {
      return this.renderedColumnsCache.value;
    }

    const columns = [
      ...(this.data?.headers || []).filter(
        (col: any) => !(col.showOnEdit && !this.isEditMode)
      ),
    ];

    if (hasDeleteColumn) {
      columns.push({
        name: 'Delete',
        prop: 'delete',
        width: 80,
        sortable: false,
      });
    }

    this.renderedColumnsCache = { key: cacheKey, value: columns };
    return columns;
  }

  onCellValueChange(rowIndex: number, prop: string, type: string, value: any) {
    let parsedValue: any = value;
    if (type === 'number') {
      // Keep raw value while typing to preserve caret position, but cap to 4 decimals.
      parsedValue = this.limitToMaxDecimals(value, 4);
    }
    this.updateCell(rowIndex, prop, parsedValue);
  }

  onInputValueChange(
    event: Event,
    rowIndex: number,
    prop: string,
    type: string
  ) {
    const input = event.target as HTMLInputElement;
    this.onCellValueChange(rowIndex, prop, type, input.value);

    if (type === 'number') {
      const normalized = this.rows[rowIndex]?.[prop] ?? '';
      if (input.value !== String(normalized)) {
        input.value = String(normalized);
      }
    }
  }

  private limitToMaxDecimals(value: any, maxDecimals: number): any {
    if (value === '' || value === null || value === undefined) return value;

    const raw = String(value).trim();
    if (raw === '-' || raw === '.' || raw === '-.') return raw;

    // Handle scientific notation safely for number inputs (e.g. 1e-7).
    if (/e/i.test(raw)) {
      const expValue = Number(raw);
      if (Number.isNaN(expValue)) return '';
      return Number(expValue.toFixed(maxDecimals));
    }

    const isNegative = raw.startsWith('-');
    const numericBody = raw.replace(/[^0-9.]/g, '');
    const firstDotIndex = numericBody.indexOf('.');

    const normalizedBody =
      firstDotIndex === -1
        ? numericBody
        : `${numericBody.slice(0, firstDotIndex)}.${numericBody
          .slice(firstDotIndex + 1)
          .replace(/\./g, '')}`;

    let normalized = isNegative ? `-${normalizedBody}` : normalizedBody;
    if (normalized === '' || normalized === '-') return normalized;

    const decimalIndex = normalized.indexOf('.');
    if (decimalIndex === -1) return normalized;

    const integerPart = normalized.slice(0, decimalIndex);
    const decimalPart = normalized.slice(decimalIndex + 1);
    return `${integerPart}.${decimalPart.slice(0, maxDecimals)}`;
  }

  private roundToMaxDecimals(value: number, maxDecimals: number): number {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(maxDecimals));
  }

  private resolveInvestmentEstimateProp(row: any): string {
    const headerProps: string[] = (this.data?.headers || [])
      .map((col: any) => col?.prop)
      .filter((prop: any) => typeof prop === 'string' && prop.length > 0);

    const knownKeys = [
      'investment_estimate',
      'ideation_investment_estimate',
      'aop_investment_estimate',
      'simulation_investment_estimate',
    ];

    const dynamicKeys = headerProps.filter((prop) =>
      /investment_estimate$/i.test(prop)
    );

    const candidates = [...new Set([...knownKeys, ...dynamicKeys])];
    const rowKeys = Object.keys(row || {});

    const fromRow = candidates.find((key) => rowKeys.includes(key));
    if (fromRow) return fromRow;

    const fromHeader = candidates.find((key) => headerProps.includes(key));
    if (fromHeader) return fromHeader;

    return 'investment_estimate';
  }

  private setInvestmentEstimate(row: any, value: number): void {
    const estimateProp = this.resolveInvestmentEstimateProp(row);
    row[estimateProp] = this.roundToMaxDecimals(value, 4);
  }
  private roundToDecimals(value: number, decimals: number = 4): number {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(decimals));
  }

  onSelectChange(
    event: any,
    rowIndex: number,
    prop: string,
    column: any
  ): void {
    if (column.isMultiSelect) {
      this.rows[rowIndex][prop] = Array.isArray(event) ? event : [event];
    } else {
      this.rows[rowIndex][prop] = event;
    }

    this.updateCell(rowIndex, prop, this.rows[rowIndex][prop]);
  }

  private getProjectCategory(): string {
    const additionalFilters = this.data?.additionalFilters;
    if (!Array.isArray(additionalFilters)) return '';

    const projectCategoryFilter = additionalFilters.find(
      (filter: any) => filter?.project_category
    );

    const categoryValue = projectCategoryFilter?.project_category;
    if (Array.isArray(categoryValue)) {
      return String(categoryValue[0] ?? '').toLowerCase();
    }

    return String(categoryValue ?? '').toLowerCase();
  }

  private isAopPlanningPage(): boolean {
    if (this.getProjectCategory() === 'aop') return true;

    const headerProps = new Set(
      (this.data?.headers || []).map((column: any) => String(column?.prop || ''))
    );
    const attributeNames = Array.isArray(this.data?.attributeNames)
      ? this.data.attributeNames.map((name: any) => String(name || ''))
      : [];

    return (
      headerProps.has('aop_final_score') ||
      headerProps.has('aop_investment_estimate') ||
      attributeNames.includes('aop_final_score') ||
      attributeNames.includes('aop_investment_estimate')
    );
  }

  private isIdeasHopperPage(): boolean {
    return this.getProjectCategory() === 'ideation';
  }

  private isSimulationPlanningPage(): boolean {
    return this.getProjectCategory() === 'simulation';
  }

  updateCell(rowIndex: number, prop: string, value: any) {
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    const row = this.rows[rowIndex];
    if (row && typeof row === 'object') {
      this.rowHeightCache.delete(row);
    }
    this.rows[rowIndex][prop] = value;
    if (this.rows[rowIndex].row_status !== 'new') {
      this.rows[rowIndex].row_status = 'edited';
    }
    // Auto-update total investment if relevant fields are updated
    if (
      ['investment_parts', 'investment_people', 'investment_testing'].includes(
        prop
      )
    ) {
      const row = this.rows[rowIndex];
      const parts = Number(row['investment_parts']) || 0;
      const people = Number(row['investment_people']) || 0;
      const testing = Number(row['investment_testing']) || 0;
      this.setInvestmentEstimate(row, parts + people + testing);
      const total = this.roundToDecimals(parts + people + testing, 4);

      if (this.isAopPlanningPage()) {
        row['aop_investment_estimate'] = total;
      }

      if (this.isIdeasHopperPage()) {
        row['ideation_investment_estimate'] = total;
      }
    }
    if (
      [
        'investment_year_26',
        'investment_year_27',
        'investment_year_28',
        'investment_year_29',
        'investment_year_30',
        'investment_year_31',
      ].includes(prop)
    ) {
      const row = this.rows[rowIndex];
      const x2 = Number(row['investment_year_27']) || 0;
      const x3 = Number(row['investment_year_28']) || 0;
      const x4 = Number(row['investment_year_29']) || 0;
      const x5 = Number(row['investment_year_30']) || 0;
      const x6 = Number(row['investment_year_31']) || 0;
      this.setInvestmentEstimate(row, x2 + x3 + x4 + x5 + x6);
      const total = this.roundToDecimals(x2 + x3 + x4 + x5 + x6, 4);

      if (this.isSimulationPlanningPage()) {
        row['simulation_investment_estimate'] = total;
      }

    }
    this.note = 'Record is not saved until save button is clicked';
    this.changed = true;
  }

  deleteRow(index: number) {
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.rows[index].is_deleted = true;
    this.rows = [...this.rows];
    this.rows[index].row_status = 'deleted';
    this.note = 'Record is not saved until save button is clicked';
    this.changed = true;
  }

  addRow() {
    const newRow: any = {};
    this.data.headers.forEach((col: any) => {
      if (col.prop !== 'delete') {
        if (col.type === 'number') {
          newRow[col.prop] = 0;
        } else if (col.type === 'dropdown') {
          //  set default selected value
          newRow[col.prop] = col.selected ?? '';
        } else if (col.type === 'date') {
          newRow[col.prop] = null;
        } else {
          newRow[col.prop] = '';
        }
      }
    });
    newRow['row_status'] = 'new';
    this.rows = [newRow, ...this.rows];
    this.resetToFirstPage();
    this.dropdownDisplayCache = {};
    this.dropdownVisibilityCache = {};
    this.note = 'Record is not saved until save button is clicked';
    this.changed = true;
  }

  private resetToFirstPage(): void {
    if (!this.table) return;
    this.table.offset = 0;
    this.table.bodyComponent?.updateOffsetY(0);
    this.table.bodyComponent?.updateRows();
  }

  onDateChange(rowIndex: number, prop: string, date: Date | null) {
    this.rows[rowIndex][prop] = date; // keep as Date object
  }

  selectFilter(filter: string, checked: boolean) {
    if (checked) {
      // Add filter if not already present
      if (!this.selectedFilters.includes(filter)) {
        this.selectedFilters.push(filter);
      }
    } else {
      // Remove unchecked filter
      this.selectedFilters = this.selectedFilters.filter(
        (f: any) => f !== filter
      );
    }

    // Reload your chart with the updated active filters
    this.loadAndRenderChart({
      ...this.filters,
      activeFilters: [...this.selectedFilters], // spread to avoid mutation
    });
  }

  save() {
    let changedRows = this.rows.filter(
      (row: any) =>
        row.row_status === 'edited' ||
        row.row_status === 'new' ||
        row.row_status === 'deleted'
    );

    if (!changedRows || changedRows.length === 0) {
      this.snackBar.open('No changes to save.', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
      this.isLoading = false; // Also stop isLoading here
      return;
    }

    const modifiedCount = changedRows.filter(
      (row: any) => row.row_status === 'edited' || row.row_status === 'new'
    ).length;
    const deletedCount = changedRows.filter(
      (row: any) => row.row_status === 'deleted' || row.is_deleted
    ).length;

    if (deletedCount > 0) {
      const proceed = globalThis.confirm(
        `You are modifying ${modifiedCount} records, deleting ${deletedCount} records.\nPlease click OK to proceed.`
      );

      if (!proceed) {
        return;
      }
    }

    this.isLoading = true;

    const mandatoryColumns = (this.data?.headers || []).filter(
      (column: any) => column?.mandatory === true
    );

    if (mandatoryColumns.length > 0) {
      const missingMandatoryColumnNames = new Set<string>();

      changedRows.forEach((row: any) => {
        if (row.is_deleted) return;

        mandatoryColumns.forEach((column: any) => {
          const value = row[column.prop];
          if (this.isBlank(value)) {
            missingMandatoryColumnNames.add(column.name || column.prop);
          }
        });
      });

      if (missingMandatoryColumnNames.size > 0) {
        const missingFields = Array.from(missingMandatoryColumnNames);
        const fieldNamesList = missingFields.map((name) => `• ${name}`).join('\n');
        this.isLoading = false;
        this.snackBar.open(
          `Please fill mandatory field(s):\n${fieldNamesList}`,
          'Close',
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error', 'snackbar-multiline'],
          }
        );
        return;
      }
    }

    const numberColumnProps = new Set<string>(
      (this.data?.headers || [])
        .filter((column: any) => column?.type === 'number')
        .map((column: any) => column.prop)
    );

    // Remove row_status and normalize configured number fields before sending.
    changedRows = changedRows.map(({ row_status, ...rest }) => {
      const normalizedRow: any = { ...rest };

      numberColumnProps.forEach((prop) => {
        if (!(prop in normalizedRow)) return;

        const value = normalizedRow[prop];
        if (value === '' || value === null || value === undefined) return;

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
          normalizedRow[prop] = Number(numericValue.toFixed(4));
        }
      });

      return normalizedRow;
    });

    this.configService
      .saveData(changedRows, this.filters?.source?.[0], this.data, this.email)
      .subscribe({
        next: (res: any) => {
          const commitStatus = this.getCommitStatus(res);
          if (commitStatus !== 'COMMIT_SUCCESS') {
            this.isLoading = false;
            this.snackBar.open(
              `Failed to Save: Commit Status ${commitStatus || 'UNKNOWN'}`,
              'Close',
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snackbar-error'],
              }
            );
            return;
          }

          this.snackBar.open('Records updated successfully!', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-success'],
          });
          this.changed = false;
          this.note = '';
          this.getRows();
        },
        error: (err) => {
          console.error('Save failed:', err);
          this.isLoading = false;
          this.snackBar.open('Failed to save.', 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  private getCommitStatus(response: any): string {
    const status =
      response?.commitStatus ??
      response?.data?.commitStatus ??
      response?.responseList?.[0]?.commitStatus ??
      response?.responseList?.[0]?.data?.commitStatus;

    return String(status || '').toUpperCase();
  }

  refresh() {
    // Keep existing columnFilters so reload does not reset user-applied filters.
    this.clearUiCaches();
    this.searchText = '';
    this.loadAndRenderChart(this.filters);
  }

  private scheduleTableRecalculate(): void {
    if (!this.table) {
      return;
    }

    setTimeout(() => {
      this.table?.recalculate?.();
      this.table?.bodyComponent?.updateRows?.();
    });
  }
  ngOnDestroy(): void {
    this.stopCacheReloadTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadCSV() {
    let filePath = '/assets/ecosystem.csv'; // Adjust path as needed
    const source = this.filters?.source?.[0]?.toLowerCase();
    switch (source) {
      case 'vpcr':
        filePath = '/assets/downloadFiles/[vpcr]ecosystem.csv';
        break;
      case 'vpi':
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
      case 'pps/implementation':
        filePath = '/assets/downloadFiles/[pps]ecosystem.csv';
        break;
      case 'fap':
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
      default:
        filePath = '/assets/downloadFiles/ecosystem.csv';
        break;
    }
    const link = document.createElement('a');
    link.href = filePath;
    link.click();
  }

  downloadData() {
    this.isLoading = true;
    const filters = { ...this.filters, ...this.columnFilters };
    if (this.data.downloadRequestKey) {
      this.configService
        .fetchData2(
          this.data.downloadDataUrl,
          this.data.downloadFunctionName,
          filters,
          this.data,
          'post',
          { responseType: 'blob' as 'json', observe: 'response' as 'body' }
        )
        .subscribe({
          next: (response: any) => {
            const res: Blob = response.body; // The blob data

            //extract filename from headers
            let filename = 'exported_data.xlsx';
            const contentDisposition = response.headers?.get(
              'Content-Disposition'
            );
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?(.+)"?/);
              if (match && match[1]) filename = match[1];
            }

            // Create download link
            const blob = new Blob([res], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isLoading = false
          },
          error: ((err) => {
            this.isLoading = false;
            console.error('Download error:', err);
          })
        });
    }
    else {
      this.configService
        .fetchData(
          this.data.downloadDataUrl,
          this.data.downloadFunctionName,
          filters,
          this.data,
          'post',
          { responseType: 'blob' as 'json', observe: 'response' as 'body' }
        )
        .subscribe({
          next: (response: any) => {
            const res: Blob = response.body; // The blob data

            //extract filename from headers
            let filename = 'exported_data.xlsx';
            const contentDisposition = response.headers?.get(
              'Content-Disposition'
            );
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?(.+)"?/);
              if (match && match[1]) filename = match[1];
            }

            // Create download link
            const blob = new Blob([res], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isLoading = false
          },
          error: ((err) => {
            this.isLoading = false;
            console.error('Download error:', err);
          })
        });
    }
  }

  downloadExcel() {
    this.isLoading = true;

    const filters = { ...this.filters, ...this.columnFilters };
    const downloadExcelFunctionName =
      this.resolveDownloadExcelFunctionName(filters);
    const exportColumns = this.buildExportColumns();

    this.configService.downloadExcel(
      this.data.downloadExcelDataUrl,
      downloadExcelFunctionName,
      filters,
      this.data,
      this.email,
      exportColumns
    ).subscribe({
      next: (data: any) => {
        this.isLoading = false;

        if (data?.data?.fileContent) {
          try {
            let base64 = data.data.fileContent;

            //  Debug logs (optional – can remove later)
            console.log('fileContent sample:', base64.substring(0, 100));
            console.log('fileContent length:', base64.length);

            //  Normalize base64
            base64 = base64.replace(/\s/g, '');
            const padding = base64.length % 4;
            if (padding) {
              base64 += '='.repeat(4 - padding);
            }

            // Decode base64
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const blob = new Blob([new Uint8Array(byteNumbers)], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            //  Filename fallback
            const filename = data.data.fileName || 'exported_data.xlsx';

            //  Trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

          } catch (e) {
            console.error('Error processing base64 file content:', e);
            this.snackBar.open('Failed to decode downloaded file.', 'Close', {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
        } else {
          console.error('Invalid download response:', data);
          this.snackBar.open(
            'Download failed: Response did not contain file content.',
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            }
          );
        }
      },

      error: (err: any) => {
        this.isLoading = false;
        console.error('Download error:', err);
        this.snackBar.open('Download failed.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      }
    });
  }

  private buildExportColumns(): Array<{ prop: string; name: string }> {
    const headers = Array.isArray(this.data?.headers) ? this.data.headers : [];

    return headers
      .filter((col: any) => !(col?.showOnEdit && !this.isEditMode))
      .map((col: any) => ({
        prop: String(col?.prop ?? '').trim(),
        name: String(col?.name ?? col?.prop ?? '').trim(),
      }))
      .filter((col: { prop: string; name: string }) => !!col.prop);
  }

  private resolveDownloadExcelFunctionName(filters: any): string {
    const baseFunctionName = String(this.data?.downloadExcelFunctionName || '');
    const supportedBaseNames = new Set([
      'SimPlanningExport',
      'AOPlanningExport',
      'IdeasHopperExport',
    ]);

    if (!supportedBaseNames.has(baseFunctionName)) {
      return baseFunctionName;
    }

    const sourceValue = filters?.source;
    const normalizedSource = Array.isArray(sourceValue)
      ? String(sourceValue[0] ?? '').trim().toUpperCase()
      : String(sourceValue ?? '').trim().toUpperCase();

    if (normalizedSource === 'VPI') {
      return `${baseFunctionName}_VPI`;
    }

    if (normalizedSource === 'FAP') {
      return `${baseFunctionName}_FAP`;
    }

    // Default includes ALL/VPCR/empty source.
    return baseFunctionName;
  }



  private _getFileExtension(filename: string): string {
    const index = filename.lastIndexOf('.');
    return index !== -1 ? filename.substring(index) : '';
  }

  private _handleUpload(uploadObservable: Observable<any>) {
    uploadObservable.subscribe({
      next: (res: any) => {
        this.snackBar.open(res.message, 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
        this.refresh();
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Upload error', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  upload() {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { excel: false },
    });

    dialogRef.afterClosed().subscribe((file: File | null) => {
      if (!file) return;
      this.isLoading = true;
      const uploadServiceObservable = this.configService.upload(
        file,
        this.data.uploadUrl,
        this.filters.project_category[0],
        this.email
      );
      this._handleUpload(uploadServiceObservable);
    });
  }

  uploadExcel() {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { excel: true, projectCategory: this.filters.project_category[0] },
    });

    dialogRef.afterClosed().subscribe((file: File | null) => {
      if (!file) return;
      this.isLoading = true;
      const uploadServiceObservable = this.appConfigService.uploadexcel(
        file,
        this.data.uploadExcelUrl,
        this.filters.project_category[0],
        this.email
      );
      this._handleUpload(uploadServiceObservable);
    });
  }

  onPinColumn(column: any, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!column?.prop) return;

    this.pinnedColumnProp =
      this.pinnedColumnProp === column.prop ? null : column.prop;

    if (this.table) {
      this.table.recalculate();
      this.table.bodyComponent?.updateRows();
    }
    this.cdr.detectChanges();
  }

  isColumnPinned(column: any): boolean {
    return !!column?.prop && this.pinnedColumnProp === column.prop;
  }

  isColumnFrozen(column: any, columnIndex: number): boolean {
    if (column?.prop === 'delete') {
      return false;
    }

    if (this.pinnedColumnProp) {
      const pinnedIndex = (this.data?.headers || []).findIndex(
        (header: any) => header?.prop === this.pinnedColumnProp
      );
      return pinnedIndex >= 0 && columnIndex <= pinnedIndex;
    }

    return column?.freeze === true || column?.frozenLeft === true;
  }

  private resetPinnedColumnState(): void {
    if (!this.pinnedColumnProp) return;

    const hasPinnedColumn = (this.data?.headers || []).some(
      (header: any) => header?.prop === this.pinnedColumnProp
    );

    if (!hasPinnedColumn) {
      this.pinnedColumnProp = null;
    }
  }

  getHeaderClass(column: any) {
    const cacheKey = String(column?.prop || column?.name || 'default');
    if (this.headerClassCache[cacheKey] !== undefined) {
      return this.headerClassCache[cacheKey];
    }

    const className = column.auto ? 'auto-header' : '';
    this.headerClassCache[cacheKey] = className;
    return className;
  }
  getCellClass(column: any) {
    const cacheKey = String(column?.prop || column?.name || 'default');
    if (this.cellClassCache[cacheKey] !== undefined) {
      return this.cellClassCache[cacheKey];
    }

    const requestedAlign = String(
      column?.text_align ||
        (column?.type === 'number' ? 'right' : 'left')
    ).toLowerCase();
    const align = ['left', 'center', 'right'].includes(requestedAlign)
      ? requestedAlign
      : 'left';
    const className = `align-${align}`;
    this.cellClassCache[cacheKey] = className;
    return className;
  }
  getColumnTotal(columnKey: string): number {
    const data =
      this.selectedRows && this.selectedRows.length > 0
        ? this.selectedRows
        : this.rows;
    if (data.length === 0) return 0;

    const total = data.reduce((sum: number, row: any) => {
      const value = parseFloat(row[columnKey]) || 0;
      return sum + value;
    }, 0);

    return this.roundToDecimals(total, 4);
  }

  applyFilters(event?: string) {
    const activeFilterKeys = Object.keys(this.columnFilters).filter((col) => {
      const selectedValues = this.columnFilters[col];
      return Array.isArray(selectedValues) && selectedValues.length > 0;
    });

    if (activeFilterKeys.length === 0) {
      this.rows = [...this.originalRows];
      this.applyCurrentSort();
      if (event === 'close') {
        this.closeMenu();
      }
      return;
    }

    this.rows = this.originalRows.filter((row) => {
      return activeFilterKeys.every((col) => {
        const selectedValues = this.columnFilters[col];

        const cellValue = row[col];

        // If the cell has multiple values (multi-select)
        if (Array.isArray(cellValue)) {
          // Check if any selected value matches any in cellValue
          return cellValue.some((v) => selectedValues.includes(v));
        }

        // For single values, simple includes check
        return selectedValues.includes(cellValue);
      });
    });

    this.applyCurrentSort();

    if (event === 'close') {
      this.closeMenu();
    }
  }

  clearAllFilters() {
    Object.keys(this.columnFilters).forEach((key) => {
      this.columnFilters[key] = [];
    });
    this.searchText = '';
    this.uniqueValuesCache = {};
    this.dropdownVisibilityCache = {};
    this.rows = [...this.originalRows];
    this.applyFilters();
  }

  clearFilter(columnProp: string | undefined) {
    if (!columnProp) return;
    this.columnFilters[columnProp] = [];
    this.searchText = '';
    this.uniqueValuesCache = {};
    this.dropdownVisibilityCache = {};
    this.applyFilters();
    this.closeMenu();
  }

  onFilterChange(column: any, value: any, checked: boolean) {
    if (!this.columnFilters[column]) this.columnFilters[column] = [];
    if (checked) {
      this.columnFilters[column].push(value);
    } else {
      this.columnFilters[column] = this.columnFilters[column].filter(
        (v: any) => v !== value
      );
    }
  }

  searchText: string = '';
  uniqueValuesCache: { [key: string]: any[] } = {};
  activeColumn: any = null;

  filterSearch(columnProp: string | undefined, event: any): void {
    if (!columnProp) return;
    this.searchText = event.target.value || '';
  }

  getUniqueValues(columnProp: string | undefined): any[] {
    if (!columnProp) return [];

    if (!this.uniqueValuesCache[columnProp]) {
      // Flatten values safely, supporting arrays and primitives
      const allValues = this.originalRows.flatMap((row: any) => {
        const value = row[columnProp];

        if (Array.isArray(value)) {
          return value; // multi-select → expand values
        } else if (value !== null && value !== undefined) {
          return [value]; // single value
        } else {
          return []; // skip null/undefined
        }
      });

      // Deduplicate while keeping primitives clean
      this.uniqueValuesCache[columnProp] = Array.from(new Set(allValues));
    }

    const allUnique = this.uniqueValuesCache[columnProp];

    // Apply case-insensitive search if needed
    const searchText = this.searchText.trim().toLowerCase();
    if (!searchText) return allUnique;
    return allUnique.filter((v) =>
      (v ?? '').toString().toLowerCase().includes(searchText)
    );
  }

  isValueChecked(columnProp: string | undefined, value: any): boolean {
    if (!columnProp) return false;
    return this.columnFilters[columnProp]?.includes(value) ?? false;
  }

  activeTrigger: MatMenuTrigger | null = null;
  openFilter(column: any, trigger: MatMenuTrigger, event: MouseEvent) {
    event.stopPropagation();

    // Close any previously open filter menu
    if (this.activeTrigger && this.activeTrigger !== trigger) {
      this.activeTrigger.closeMenu();
    }

    this.activeColumn = column;
    this.activeTrigger = trigger;
    this.searchText = '';

    // Open the new menu
    trigger.openMenu();
  }

  closeMenu(event?: Event) {
    event?.stopPropagation();
    if (this.activeTrigger) {
      this.activeTrigger.closeMenu();
      this.activeTrigger = null;
    }
    this.activeColumn = null;
    this.searchText = '';
  }

  onSort(event: any) {
    const sort = event?.sorts?.[0];
    if (!sort?.prop) {
      return;
    }

    const prop = this.isUserHistoryPage() ? 'updated_date' : sort.prop;
    const dir: 'asc' | 'desc' = this.isUserHistoryPage()
      ? 'desc'
      : sort.dir === 'desc'
        ? 'desc'
        : 'asc';
    this.sortState = { prop, dir };

    this.rows = this.sortRows(this.rows, prop, dir);
    this.scheduleTableRecalculate();
  }

  /** Check if all values are selected */
  areAllSelected(columnProp: string | undefined): boolean {
    if (!columnProp) return false;
    const allValues = this.getUniqueValues(columnProp);
    const selectedValues = this.columnFilters[columnProp] || [];
    return (
      allValues.length > 0 && allValues.every((v) => selectedValues.includes(v))
    );
  }

  /** Check if partially selected */
  selectPerOne(columnProp: string | undefined): boolean {
    if (!columnProp) return false;
    const allValues = this.getUniqueValues(columnProp);
    const selectedValues = this.columnFilters[columnProp] || [];
    return (
      selectedValues.length > 0 && selectedValues.length < allValues.length
    );
  }

  /** Toggle all selections */
  toggleAll(columnProp: string | undefined, checked: boolean): void {
    if (!columnProp) return;
    const allValues = this.getUniqueValues(columnProp);

    if (checked) {
      // Select all visible (filtered) values
      this.columnFilters[columnProp] = [...allValues];
    } else {
      // Deselect all
      this.columnFilters[columnProp] = [];
    }
  }

  //Highlighting unmatched values in dropdown columns

  isBlank(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') return value.trim() === '';
    return false;
  }

  isValueInList(value: any, list: any[]): boolean {
    if (this.isBlank(value)) return true; // ignore blanks
    return list.some((opt) => (opt.value ?? opt) === value);
  }

  getUnmatchedValues(values: any[], list: any[]): any[] {
    if (!Array.isArray(values)) return [];
    const validValues = list.map((opt) => opt.value ?? opt);
    return values.filter((v) => !this.isBlank(v) && !validValues.includes(v));
  }

  shouldEnableDropdownSearch(column: any): boolean {
    return !!column?.enableSearch;
  }

  private getDropdownSearchKey(rowIndex: number, columnProp: string): string {
    return `${rowIndex}::${columnProp}`;
  }

  getDropdownSearchText(rowIndex: number, columnProp: string): string {
    return this.dropdownSearchText[this.getDropdownSearchKey(rowIndex, columnProp)] || '';
  }

  onDropdownSearchInput(rowIndex: number, columnProp: string, value: string): void {
    this.dropdownVisibilityCache = {};
    this.dropdownSearchText[this.getDropdownSearchKey(rowIndex, columnProp)] = value || '';
  }

  getFilteredDropdownOptions(column: any, rowIndex: number): any[] {
    const options = Array.isArray(column?.list) ? column.list : [];
    if (!this.shouldEnableDropdownSearch(column) || !column?.prop) {
      return options;
    }

    const searchText = this.getDropdownSearchText(rowIndex, column.prop).trim().toLowerCase();

    if (!searchText) {
      return options;
    }

    return options.filter((option: any) => {
      const label = String(option?.viewValue ?? option ?? '')
        .trim()
        .toLowerCase();
      return label.includes(searchText);
    });
  }

  isDropdownOptionVisible(option: any, column: any, rowIndex: number): boolean {
    if (!this.shouldEnableDropdownSearch(column) || !column?.prop) {
      return true;
    }

    const searchText = this.getDropdownSearchText(rowIndex, column.prop).trim().toLowerCase();
    if (!searchText) {
      return true;
    }

    const optionKey = this.getDropdownOptionKey(option?.value ?? option);
    const visibilityCacheKey = `${rowIndex}::${column.prop}::${searchText}::${optionKey}`;
    const cachedVisibility = this.dropdownVisibilityCache[visibilityCacheKey];
    if (cachedVisibility !== undefined) {
      return cachedVisibility;
    }

    const label = String(option?.viewValue ?? option ?? '').trim().toLowerCase();
    const isVisible = label.includes(searchText);
    this.dropdownVisibilityCache[visibilityCacheKey] = isVisible;
    return isVisible;
  }

  getDropdownOptionsForDisplay(column: any, rowIndex: number): any[] {
    const options = Array.isArray(column?.list) ? column.list : [];
    if (!column?.prop || options.length === 0) {
      return options;
    }

    const selectedValue = this.rows?.[rowIndex]?.[column.prop];
    const selectedValues = column.isMultiSelect
      ? this.normalizeMultiSelectValue(selectedValue)
      : this.isBlank(selectedValue)
        ? []
        : [selectedValue];

    if (selectedValues.length === 0) {
      return options;
    }

    const selectedSignature = selectedValues
      .map((value: any) => this.getDropdownOptionKey(value))
      .sort()
      .join('|');
    const displayCacheKey = `${rowIndex}::${column.prop}::${selectedSignature}`;
    const cachedDisplayOptions = this.dropdownDisplayCache[displayCacheKey];
    if (cachedDisplayOptions) {
      return cachedDisplayOptions;
    }

    const selectedKeys = new Set(
      selectedValues.map((value: any) => this.getDropdownOptionKey(value))
    );

    const selectedOptions: any[] = [];
    const unselectedOptions: any[] = [];

    options.forEach((option: any) => {
      const optionKey = this.getDropdownOptionKey(option?.value ?? option);
      if (selectedKeys.has(optionKey)) {
        selectedOptions.push(option);
      } else {
        unselectedOptions.push(option);
      }
    });

    const orderedOptions = [...selectedOptions, ...unselectedOptions];
    this.dropdownDisplayCache[displayCacheKey] = orderedOptions;
    return orderedOptions;
  }

  trackByDropdownOption = (_index: number, option: any): string => {
    return this.getDropdownOptionKey(option?.value ?? option);
  };

  trackByHeader = (_index: number, column: any): string => {
    return String(column?.prop ?? column?.name ?? _index);
  };

  isDeleteColumn(column: any): boolean {
    return String(column?.prop ?? '') === 'delete';
  }

  openAuditHistory(row: any): void {
    const titleValue = String(row?.title ?? '').trim();
    const popupHeaders = this.getAuditPopupHeadersForRow(row);
    const allowedAuditFields = (popupHeaders || [])
      .map((header: any) => String(header?.prop ?? '').trim())
      .filter((key: string) => !!key);
    const fieldLabelMap = (popupHeaders || []).reduce(
      (acc: Record<string, string>, header: any) => {
        const key = String(header?.prop ?? '').trim();
        const label = String(header?.name ?? '').trim();
        if (key && label) {
          acc[key] = label;
        }
        return acc;
      },
      {}
    );
    const auditRequestFilter = this.buildAuditHistoryPayload(titleValue);

    this.dialog.open(PopupTableComponent, {
        width: '1200px',
      height: '700px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        title: 'Audit History',
        headers: [
          { key: 'field', label: 'Field', width: 220 },
          { key: 'oldValue', label: 'Old Value', width: 220 },
          { key: 'newValue', label: 'New Value', width: 220 },
          { key: 'updatedBy', label: 'Updated By', width: 200 },
          { key: 'updatedOn', label: 'Updated On', width: 180 },
        ],
        // Placeholder rows so popup works for all tables now.
        rows: [
          {
            field: 'Pending payload',
            oldValue: '-',
            newValue: '-',
            updatedBy: '-',
            updatedOn: '-',
          },
        ],
        apiUrl: this.data?.auditHistoryApiUrl || '/spriced/platform/getData',
        requestPayload: auditRequestFilter,
        useRawPayload: true,
        applyAuditFieldHeaderMapping: this.shouldApplyScopedAuditMapping(),
        allowedAuditFields,
        fieldLabelMap,
        sourceRow: row,
      },
    });
  }

  isDetailHistoryColumn(column: any): boolean {
    return String(column?.prop ?? '').trim().toLowerCase() === 'detail_history';
  }

  navigateToDetailedHistory(row: any): void {
    const routeSource =
      row?.source ??
      row?.table_name ??
      row?.project_category ??
      this.filters?.table_name?.[0] ??
      '';
    const targetRoute = this.getAuditRouteBySource(routeSource);
    if (!targetRoute) {
      const sourceText = String(routeSource ?? '').trim() || 'Unknown';
      this.snackBar.open(
        `Unable to resolve target page for source: ${sourceText}`,
        'Close',
        {
          duration: 3500,
        }
      );
      return;
    }

    const queryParams: Record<string, string> = {
      openAudit: '1',
      title: String(row?.title ?? '').trim(),
      updatedBy: String(row?.updated_by ?? '').trim(),
      updatedDate: String(row?.updated_date ?? '').trim(),
      source: String(routeSource ?? '').trim(),
    };

    this.router.navigate([this.withCurrentRoutePrefix(targetRoute)], { queryParams });
  }

  private getAuditRouteBySource(source: any): string {
    const normalized = String(source ?? '').trim().toUpperCase();
    if (normalized.includes('IDEA') || normalized.includes('HOPPER')) {
      return '/ideas-hopper';
    }
    if (normalized.includes('SIM')) {
      return '/sim-planning';
    }
    if (normalized.includes('AOP')) {
      return '/aop-planning-2026';
    }

    const sourceRouteMap: Record<string, string> = {
      IDEAS_HOPPER: '/ideas-hopper',
      'IDEAS HOPPER': '/ideas-hopper',
      IDEATION: '/ideas-hopper',
      SIM_PLANNING: '/sim-planning',
      'SIM PLANNING': '/sim-planning',
      SIMULATION: '/sim-planning',
      AOP_PLANNING: '/aop-planning-2026',
      'AOP PLANNING': '/aop-planning-2026',
      AOP: '/aop-planning-2026',
    };
    return sourceRouteMap[normalized] || '';
  }

  private maybeOpenAuditHistoryFromNavigation(): void {
    if (this.hasHandledAuditDeepLink || this.isUserHistoryPage()) {
      return;
    }

    const url = new URL(window.location.href);
    const shouldOpenAudit = url.searchParams.get('openAudit') === '1';
    if (!shouldOpenAudit) {
      return;
    }

    this.hasHandledAuditDeepLink = true;
    const title = String(url.searchParams.get('title') ?? '').trim().toLowerCase();
    const updatedBy = String(url.searchParams.get('updatedBy') ?? '').trim().toLowerCase();
    const source = String(url.searchParams.get('source') ?? '').trim().toLowerCase();
    const updatedDate = String(url.searchParams.get('updatedDate') ?? '').trim();

    const normalizeValue = (value: any): string =>
      String(value ?? '').trim().toLowerCase();
    const normalizeDateMillis = (value: any): number => {
      const time = new Date(String(value ?? '').trim()).getTime();
      return Number.isNaN(time) ? Number.NaN : time;
    };
    const deepLinkMillis = normalizeDateMillis(updatedDate);

    const candidates = (this.rows || []).filter((row: any) => {
      if (title && normalizeValue(row?.title) !== title) return false;
      if (updatedBy && normalizeValue(row?.updated_by) !== updatedBy) return false;
      if (source && normalizeValue(row?.source) !== source) return false;
      if (!updatedDate) return true;

      const rowMillis = normalizeDateMillis(row?.updated_date);
      if (Number.isNaN(deepLinkMillis) || Number.isNaN(rowMillis)) return true;
      return rowMillis === deepLinkMillis;
    });

    const matchedRow = candidates[0];
    if (!matchedRow) {
      this.snackBar.open('Could not find matching row for detailed history.', 'Close', {
        duration: 3000,
      });
      return;
    }

    setTimeout(() => this.openAuditHistory(matchedRow), 0);
  }

  private withCurrentRoutePrefix(targetRoute: string): string {
    const cleanTarget = `/${String(targetRoute || '').replace(/^\/+/, '')}`;
    const routePath = String(window?.location?.pathname ?? '')
      .replace(/\/+$/, '');
    const marker = '/user-history';
    const markerIndex = routePath.toLowerCase().lastIndexOf(marker);
    if (markerIndex < 0) {
      return cleanTarget;
    }
    const prefix = routePath.slice(0, markerIndex);
    return `${prefix}${cleanTarget}` || cleanTarget;
  }

  private shouldApplyScopedAuditMapping(): boolean {
    const routePath = String(window?.location?.pathname ?? '')
      .toLowerCase()
      .replace(/\/+$/, '');
    return new Set([
      '/ideas-hopper',
      '/sim-planning',
      '/aop-planning',
      '/aop-planning-2026',
      '/aop-planning-2027',
    ]).has(
      routePath
    );
  }

  private buildAuditHistoryPayload(titleValue: string): any {
    return {
      entityName: 'ecosystem',
      attributesList: ['*'],
      filterList: [
        {
          attributeName: 'title',
          operator: 'IN',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: [titleValue],
        },
      ],
    };
  }

  private injectUserHistoryAuditColumns(): void {
    if (!this.isUserHistoryPage()) {
      return;
    }

    const headers = Array.isArray(this.data?.headers) ? [...this.data.headers] : [];
    const headerMap = new Map(
      headers.map((column: any) => [String(column?.prop || '').toLowerCase(), column])
    );

    const sourceColumn = headerMap.get('source') || {
      name: 'Source',
      prop: 'source',
      width: 150,
      editable: false,
    };

    const titleColumn = headerMap.get('title') || {
      name: 'Project Title',
      prop: 'title',
      width: 500,
      editable: false,
    };
    const projectNumberColumn = headerMap.get('vpcr_number') || {
      name: 'Project Number',
      prop: 'vpcr_number',
      width: 180,
      editable: false,
    };

    const updatedByColumn = headerMap.get('updated_by') || {
      name: 'Updated By',
      prop: 'updated_by',
      width: 260,
      editable: false,
    };

    const updatedDateColumn = headerMap.get('updated_date') || {
      name: 'Update Date',
      prop: 'updated_date',
      width: 220,
      editable: false,
      type: 'date',
    };
    const detailHistoryColumn = headerMap.get('detail_history') || {
      name: 'Detailed History',
      prop: 'detail_history',
      width: 160,
      editable: false,
      sortable: false,
    };
    this.data.headers = [
      { ...sourceColumn, name: 'Source', editable: false },
      { ...titleColumn, name: 'Project Title', editable: false },
      { ...projectNumberColumn, name: 'Project Number', editable: false },
      { ...updatedByColumn, name: 'Updated By', editable: false },
      { ...updatedDateColumn, name: 'Update Date', editable: false, type: 'date' },
      { ...detailHistoryColumn, name: 'Detailed History', editable: false, sortable: false },
    ];
  }

  private getAuditPopupHeadersForRow(row: any): any[] {
    const fallbackHeaders = Array.isArray(this.data?.headers)
      ? this.data.headers
      : [];
    if (!this.isUserHistoryPage() || !this.userHistoryMergedHeaderConfig) {
      return fallbackHeaders;
    }

    const sourceKey = this.resolveHistoryHeaderKey(row);
    const sourceHeaders = this.userHistoryMergedHeaderConfig?.[sourceKey]?.headers;
    if (Array.isArray(sourceHeaders) && sourceHeaders.length) {
      return sourceHeaders;
    }

    return fallbackHeaders;
  }

  private resolveHistoryHeaderKey(row: any): string {
    const config = this.userHistoryMergedHeaderConfig || {};
    const keys = Object.keys(config);
    if (!keys.length) {
      return '';
    }

    const sourceRaw = String(row?.source ?? '').trim();
    const source = sourceRaw.toLowerCase();
    if (source && config[source]) {
      return source;
    }

    if (source.includes('vpcr')) return 'vpcr';
    if (source.includes('vpi')) return 'vpi';
    if (source.includes('pps') || source.includes('implementation')) return 'pps/implementation';
    if (source.includes('fap')) return 'fap';

    return keys[0];
  }

  private formatAuditDateForPayload(value: any, endOfDay: boolean): string {
    const raw = String(value ?? '').trim().replace(/^'+|'+$/g, '');
    if (!raw) {
      return '';
    }

    const existingFormatMatch = raw.match(
      /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(?:Z|[+-]\d{2}:?\d{2}|[+-]\d{2})?$/
    );
    if (existingFormatMatch) {
      const [, datePart, timePart, fractionalRaw = ''] = existingFormatMatch;
      const millis = fractionalRaw.padEnd(3, '0').slice(0, 3);
      const normalizedRaw = `${datePart}T${timePart}.${millis}${
        /(?:Z|[+-]\d{2}:?\d{2}|[+-]\d{2})$/.test(raw) ? '' : 'Z'
      }`;
      const parsed = new Date(normalizedRaw);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDateToUtcPayload(parsed);
      }
      return `${datePart} ${timePart}.${millis}+00`;
    }

    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      // Treat picker dates as local-day boundaries, then convert to UTC.
      const localDate = endOfDay
        ? new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          23,
          59,
          59,
          999
        )
        : new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
      return this.formatDateToUtcPayload(localDate);
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
      return raw;
    }

    return this.formatDateToUtcPayload(parsedDate);
  }

  private formatDateToUtcPayload(value: Date): string {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    const hours = String(value.getUTCHours()).padStart(2, '0');
    const minutes = String(value.getUTCMinutes()).padStart(2, '0');
    const seconds = String(value.getUTCSeconds()).padStart(2, '0');
    const millis = String(value.getUTCMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${millis}+00`;
  }

  isUserHistoryPage(): boolean {
    const routePath = String(window?.location?.pathname ?? '')
      .toLowerCase()
      .replace(/\/+$/, '');
    return routePath === '/user-history' || routePath.endsWith('/user-history');
  }

  private getDropdownOptionKey(value: any): string {
    return `${typeof value}:${String(value)}`;
  }

  onSearchOptionSelection(event: any): void {
    if (event?.isUserInput && event?.source?.selected) {
      event.source.deselect();
    }
  }

  onDropdownOpenedChange(
    isOpen: boolean,
    rowIndex: number,
    columnProp: string,
    column: any
  ): void {
    if (isOpen || !this.shouldEnableDropdownSearch(column)) {
      return;
    }

    this.onDropdownSearchInput(rowIndex, columnProp, '');
  }

  hasUnmatchedValues(values: any, list: any[]): boolean {
    if (Array.isArray(values)) {
      return this.getUnmatchedValues(values, list).length > 0;
    } else {
      return !this.isBlank(values) && !this.isValueInList(values, list);
    }
  }
  //-----------------------------------------------------

  isCellInvalid(row: any, column: any) {
    // If neither highlightInvalid nor mandatory+highlightInvalid applies → valid
    if (!column.highlightInvalid) return false;

    const value = row[column.prop];

    // Mandatory blank (only if highlightInvalid=true)
    if (column.mandatory && this.isBlank(value)) {
      return true;
    }

    // Dropdown logic
    if (column.type === 'dropdown') {
      if (column.isMultiSelect) {
        return this.hasUnmatchedValues(value, column.list);
      } else {
        return !this.isValueInList(value, column.list);
      }
    }
    return false;
  }

  isRowInvalid(row: any) {
    return this.data.headers.some((col: any) => {
      if (!col.highlightInvalid) return false;
      return this.isCellInvalid(row, col);
    });
  }

  private hasScoreErrorMessage(row: any): boolean {
    const message = row?.score_error_message;
    return !this.isBlank(message);
  }

  getHighlightRed(row: any, column: any): boolean {
    if (!column?.highlightNull) return false;

    const numericValue = Number(row?.[column.prop]);
    const isFiniteValue = Number.isFinite(numericValue);

    if (column.prop === 'final_score') {
      return !isFiniteValue || numericValue === 0 || this.hasScoreErrorMessage(row);
    }

    return !isFiniteValue;
  }

  getHighlightGreen(row: any, column: any): boolean {
    if (!column?.highlightNull) return false;

    const numericValue = Number(row?.[column.prop]);
    const isFiniteValue = Number.isFinite(numericValue);

    if (column.prop === 'final_score') {
      return isFiniteValue && numericValue !== 0 && !this.hasScoreErrorMessage(row);
    }

    return isFiniteValue;
  }

  toggleInvalidFilter() {
    this.showInvalidOnly = !this.showInvalidOnly;
    this.applyInvalidFilter();
  }
  applyInvalidFilter() {
    if (this.showInvalidOnly) {
      this.rows = this.originalRows.filter((row) => this.isRowInvalid(row));
    } else {
      this.rows = [...this.originalRows]; // reset
    }

    this.applyCurrentSort();
  }
}


